import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { capitalizeAll, SelectInputOption, TimeSpan } from '@moderepo/mode-app-components';
import { createMetricsReportSubscription, deleteMetricsReportSubscription, ritzDataContext, UIFetchMetricsReportSubscriptionFilters,
    updateMetricsReportSubscription } from '@moderepo/mode-data-state-ritz';
import { UpdateMetricsReportSubscriptionParams } from '@moderepo/mode-internal-apis';
import { modeUIContext, showSuccessNotification } from '@moderepo/mode-ui-state';
import { TFunction } from 'i18next';
import produce, { Draft } from 'immer';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DayOfWeek, DEFAULT_MAX_TIMESTAMP_INPUTS, DEFAULT_MIN_TIMESTAMP_INPUTS, EntityDetailsControlBarProps, OPTIMIZE_DATE_SELECTOR } from '.';
import { EntityDetailsControlBar } from '..';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectMetricsReportSubscriptions, useSelectProject } from '../../utils/dataStateHooks';
import { NonEmptyArray, ReadonlyNonEmptyArray } from '../../utils/utils';
import { EntityDetailsTimeSpanControlSettings, SUPPORT_TIME_SPANS } from '../misc';



const createTimeSpanOptions = (trans: TFunction, enabledTimeSpans: readonly TimeSpan[]): readonly SelectInputOption<TimeSpan>[] => {
    return SUPPORT_TIME_SPANS.map((timeSpan) => {
        return {
            value   : timeSpan,
            label   : capitalizeAll(trans(`time_span.type.${timeSpan}`)),
            disabled: !enabledTimeSpans.includes(timeSpan),
        };
    });
};


/**
 * Given a timeSpan, a start timestamp, and an amount to subtract from star time, return a timestamp that is 'amount' timeSpan
 * before the current time. For example: If timeSpan is 1 day, startTime is Saturday, and amount is 3, this will return 3 days from Saturday
 * which will be Wednesday. If timeSpan is 1 month, startTime is May, and amount is 2, this will return March.
 */
export const getPreviousTimestamp = (
    timeSpan: TimeSpan, startTimestamp: number, amount: number, firstDayOfWeek?: number | undefined,
): number => {
    // We will round the currentTime to the nearest timeSpan. e.g. if timeSpan is DAY then we will round to the nearest day which will be
    // midnight where hh:mm:ss will be 00:00:00. If timeSpan is WEEK then we will round the currentTime to the beginning of the current week
    // which will be a SUNDAY, etc...
    const startTime = moment(startTimestamp).startOf(timeSpan);

    // Then we will subtract the rounded currentTime by the specified amount.
    if (timeSpan === TimeSpan.WEEK) {
        // For week, first day of the week is NOT always SUNDAY, it can depends on the 'firstDayOfWeek' param. If the user wants first day of
        // the week to be TUESDAY for example which value is 2, we will add 2 days to the first day of week.
        return startTime.add(firstDayOfWeek ?? 0, 'days').subtract(amount, 'weeks').valueOf();
    }
    return startTime.subtract(amount, timeSpan).valueOf();
};



export interface EntityDetailsTimeSpanControlBarContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly entityId: string;
    readonly timeControlSettings: EntityDetailsTimeSpanControlSettings;
    readonly timeSpan: TimeSpan;
    readonly timestamps: ReadonlyNonEmptyArray<number>;
    readonly showSubscriptionSettings: boolean;
    readonly onInputsChanged: (timeSpan: TimeSpan, timestamps: ReadonlyNonEmptyArray<number>)=> void;
}


/**
 * This container is a wrapper for the EntityDetailsTimeRangeControlBar. It contains the logic for the EntityDetailsTimeRangeControlBar
 * component.
 */
export const EntityDetailsTimeSpanControlBarContainer: React.FC<EntityDetailsTimeSpanControlBarContainerProps> = (
    {
        className, projectId, entityId, timeSpan, timestamps, timeControlSettings, showSubscriptionSettings, onInputsChanged,
    }: EntityDetailsTimeSpanControlBarContainerProps,
) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = useSelectProject(projectId);
    const { trans } = useRitzTranslation(project);



    /**
     * The state that holds the time control settings
     */
    const settings = useMemo(() => {
        return {
            minTimestampInputs  : timeControlSettings.minTimestampInputs ?? DEFAULT_MIN_TIMESTAMP_INPUTS,
            maxTimestampInputs  : timeControlSettings.maxTimestampInputs ?? DEFAULT_MAX_TIMESTAMP_INPUTS,
            firstDayOfWeek      : 0,
            optimizeDateSelector: OPTIMIZE_DATE_SELECTOR,
            enabledTimeSpans    : timeControlSettings.enabledTimeSpans ?? SUPPORT_TIME_SPANS,
        };
    }, [timeControlSettings.enabledTimeSpans, timeControlSettings.maxTimestampInputs, timeControlSettings.minTimestampInputs]);


    // Create the options for the time span input field
    const timeSpanOptions = useMemo(() => {
        return createTimeSpanOptions(trans, settings.enabledTimeSpans);
    }, [settings.enabledTimeSpans, trans]);


    /**
     * When the user selected a different timeSpan. When a timeSpan changed, we will also need to automatically change the current selected
     * timeStamp. The reason is because if the current timeSpan is DAY then the selected timestamps could be any day. If the user changed the
     * timeSpan to WEEK, the timestamps can't be just any day, they have to be 1 week apart and they have to be on the FIRST day of the week.
     * And the same for when the user changed timeStan to MONTH or YEAR.
     */
    const onTimeSpanChanged = useCallback((timeSpan: TimeSpan) => {
        // Because timeSpan changed, we need to clear and reinitialize all the timestamps

        // Start from the current time UNLESS "defaultTimeSpanOffset" is set to some value. We will add 'defaultTimeSpanOffset` timeSpan
        // from the current date/time
        const startTimestamp = moment().add(timeControlSettings.defaultTimeSpanOffset ?? 0, timeSpan).startOf(timeSpan);

        const newTimestamps: NonEmptyArray<number> = timestamps.map((timestamp, index) => {
            return getPreviousTimestamp(timeSpan, startTimestamp.valueOf(), index, settings.firstDayOfWeek);
        }) as unknown as NonEmptyArray<number>;

        onInputsChanged(timeSpan, newTimestamps);
    }, [timestamps, settings.firstDayOfWeek, onInputsChanged, timeControlSettings.defaultTimeSpanOffset]);


    /**
     * On timestamp changed by user, replace the timestamp in the list of timestamps at index with the new timestamp
     */
    const onTimestampChanged = useCallback((newTimestamp: MaterialUiPickersDate, index: number) => {
        if (newTimestamp && !Number.isNaN(newTimestamp.valueOf())) {
            // Make a copy of the list of timestamps. We can't update the timestamp at index directly because that
            // won't trigger the UI to be re-rendered.
            const newTimestamps: NonEmptyArray<number> = [...timestamps];

            // update the timestamp at index
            newTimestamps[index] = newTimestamp.valueOf();

            onInputsChanged(timeSpan, newTimestamps);
        }
    }, [timeSpan, timestamps, onInputsChanged]);


    /**
     * Add a new timestamp inputs to the list of timestamp inputs
     */
    const onAddTimestamp = useCallback(() => {
        // Create a new compare to timestamp. We will subtract 1 timeSpan from the last timestamp and use that as the new compare to timestamp
        const lastTimestamp = timestamps[timestamps.length - 1]?.valueOf() ?? Date.now();
        const newTimestamp = getPreviousTimestamp(timeSpan, lastTimestamp, 1, settings.firstDayOfWeek);
        const newTimestamps: NonEmptyArray<number> = [...timestamps, newTimestamp];

        onInputsChanged(timeSpan, newTimestamps);

    }, [timeSpan, timestamps, settings.firstDayOfWeek, onInputsChanged]);


    /**
     * Remove the last timestamp inputs from the list of timestamp inputs
     */
    const onRemoveTimestamp = useCallback(() => {
        if (timestamps.length > 1) {
            const newTimestamps: NonEmptyArray<number> = timestamps.slice(
                0, timestamps.length - 1,
            ) as unknown as NonEmptyArray<number>;
            onInputsChanged(timeSpan, newTimestamps);
        }
    }, [timeSpan, timestamps, onInputsChanged]);


    /**
     * On Prev button clicked, automatically subtract ALL timestamps by 1 timeSpan which will make them 1 timeStan earlier
     */
    const onPrevButtonClicked = useCallback(() => {
        const newTimestamps: number[] = [];

        for (let i = 0; i < timestamps.length; i += 1) {
            const timestamp = timestamps[i];
            newTimestamps.push(moment(timestamp).subtract(1, timeSpan).valueOf());    // move the timestamp 1 timeSpan earlier
        }

        onInputsChanged(timeSpan, newTimestamps as unknown as NonEmptyArray<number>);
    }, [onInputsChanged, timeSpan, timestamps]);


    /**
     * On Next button clicked, automatically add ALL timestamps by 1 timeSpan which will make them 1 timeStan later
     */
    const onNextButtonClicked = useCallback(() => {
        const newTimestamps: number[] = [];

        for (let i = 0; i < timestamps.length; i += 1) {
            const timestamp = timestamps[i];
            newTimestamps.push(moment(timestamp).add(1, timeSpan).valueOf());    // move the timestamp 1 timeSpan later
        }

        onInputsChanged(timeSpan, newTimestamps as unknown as NonEmptyArray<number>);
    }, [onInputsChanged, timeSpan, timestamps]);


    /**
     * For the next button, we don't want the user to go into the future since there is no data anyway, we will only enable the
     * button if there isn't any selected timestamp >= NOW
     */
    const canEnableNextButton = useMemo(() => {
        return !timestamps.some((timestamp) => {
            return timestamp >= Date.now();
        });
    }, [timestamps]);



    /**
     * For optimization, we will use 'useMemo' to cache the 'getSubscriptionFilters' object and passed the cached object
     * to 'useSelectMetricsReportSubscriptions'
     */
    const getSubscriptionFilters = useMemo((): UIFetchMetricsReportSubscriptionFilters => {
        return {
            projectId, entityId,
        };
    }, [projectId, entityId]);


    // Get the subscriptions for the selected entity. If the user subscribed to this entity, there should be at least 1 subscription returned.
    const subscriptions = useSelectMetricsReportSubscriptions(getSubscriptionFilters);


    // There shouldn't be more than 1 but because backend returns an array therefore we need to check the array and get the element at index 0.
    const selectedEntitySubscription = useMemo(() => {
        if (subscriptions && subscriptions.items.length > 0) {
            return subscriptions.items[0];
        }
        return undefined;
    }, [subscriptions]);


    /**
     * Because we don't make API call to save subscription options right away as the user check/uncheck the options, we have to store the
     * checked/unchecked options locally until the user clicked save.
     */
    const [unsavedSubscriptionOptions, setUnsavedSubscriptionOptions] = useState<UpdateMetricsReportSubscriptionParams>({});


    /**
     * This useEffect is used for monitoring the 'selectedEntitySubscription' object changes and automatically synchronize the
     * selectedEntitySubscription with the unsavedSubscriptionOptions. 'selectedEntitySubscription' changes when a new entity is selected
     * so we need to make sure the unsavedSubscriptionOptions state is the same.
     */
    useEffect(() => {
        if (selectedEntitySubscription) {
            setUnsavedSubscriptionOptions(() => {
                return {
                    receiveDailyReport  : selectedEntitySubscription.receiveDailyReport,
                    receiveWeeklyReport : selectedEntitySubscription.receiveWeeklyReport,
                    receiveMonthlyReport: selectedEntitySubscription.receiveMonthlyReport,
                    notifyOnlyOnAlert   : selectedEntitySubscription.notifyOnlyOnAlert,
                };
            });
        }
    }, [selectedEntitySubscription]);



    /**
     * Create the subscriptionSettings to be passed to the control bar for display
     */
    const controlBarSubscriptionSettings = useMemo((): EntityDetailsControlBarProps['subscriptionSettings'] => {
        if (selectedEntitySubscription) {
            return {
                subscribed         : true,
                subscribedToOptions: {
                    receiveDailyReport  : unsavedSubscriptionOptions.receiveDailyReport,
                    receiveWeeklyReport : unsavedSubscriptionOptions.receiveWeeklyReport,
                    receiveMonthlyReport: unsavedSubscriptionOptions.receiveMonthlyReport,
                    notifyOnlyOnAlert   : unsavedSubscriptionOptions.notifyOnlyOnAlert,
                },
            };
        }
        return {
            subscribed: false,
        };
    }, [selectedEntitySubscription, unsavedSubscriptionOptions.notifyOnlyOnAlert, unsavedSubscriptionOptions.receiveDailyReport,
        unsavedSubscriptionOptions.receiveMonthlyReport, unsavedSubscriptionOptions.receiveWeeklyReport]);


    /**
     * Handler for when the user check/uncheck the Subscribe/unsubscribe button. For this button, we will make API call to
     * create/delete subscription right away.
     */
    const onSubscriptionToggle = useCallback(async (checked: boolean) => {
        try {
            if (checked) {
                await dataDispatch(createMetricsReportSubscription({
                    projectId,
                    entityId,
                }));
                uiDispatch(showSuccessNotification('notif.entity_subscribed'));
            } else if (selectedEntitySubscription) {
                await dataDispatch(deleteMetricsReportSubscription(selectedEntitySubscription.id));
                uiDispatch(showSuccessNotification('notif.entity_unsubscribed'));
            }
        } catch (error) {
            // ignore error because dataDispatch already show error message
        }
    }, [dataDispatch, entityId, projectId, selectedEntitySubscription, uiDispatch]);



    /**
     * Handler for when the user check/uncheck one of the subscription option. For these options, we don't make API call to update the
     * subscription right away. We will store the state locally and will make API call when the user click save
     */
    const onSubscriptionOptionToggle = useCallback((option: keyof UpdateMetricsReportSubscriptionParams, checked: boolean) => {
        setUnsavedSubscriptionOptions((currentState) => {
            return produce(currentState, (draft: Draft<UpdateMetricsReportSubscriptionParams>) => {
                draft[option] = checked;
            });
        });
    }, []);


    /**
     * Handler for the cancel subscription option changes button click. We will reset the UnsavedSubscriptionOption back to the same
     * state as the selectedEntitySubscription
     */
    const onCancelSubscriptionChanges = useCallback(() => {
        setUnsavedSubscriptionOptions((currentState) => {
            return produce(currentState, (draft: Draft<UpdateMetricsReportSubscriptionParams>) => {
                // Reset the unsavedSubscriptionOptions back to what set in selectedEntitySubscription
                if (selectedEntitySubscription) {
                    draft.receiveDailyReport = selectedEntitySubscription.receiveDailyReport;
                    draft.receiveWeeklyReport = selectedEntitySubscription.receiveWeeklyReport;
                    draft.receiveMonthlyReport = selectedEntitySubscription.receiveMonthlyReport;
                    draft.notifyOnlyOnAlert = selectedEntitySubscription.notifyOnlyOnAlert;
                }
            });
        });
    }, [selectedEntitySubscription]);


    /**
     * Handler for the Save subscription option changes click. This is when we can make API call to save the subscription options.
     */
    const onSaveSubscriptionChanges = useCallback(async () => {
        if (selectedEntitySubscription) {

            // Find an attribute in 'unsavedSubscriptionOptions' and 'selectedEntitySubscription' that have different value. If we can
            // find one then that mean the unsavedSubscriptionOptions is different from the selectedEntitySubscription
            const isModified = Object.keys(unsavedSubscriptionOptions).find((key) => {
                return unsavedSubscriptionOptions[key] !== selectedEntitySubscription[key];
            }) !== undefined;

            // Make sure the options is modified before making API call.
            if (isModified) {
                try {
                    await dataDispatch(updateMetricsReportSubscription(selectedEntitySubscription.id, unsavedSubscriptionOptions));
                    uiDispatch(showSuccessNotification('notif.entity_subscription_updated'));
                } catch (error) {
                    // ignore error because dataDispatch already show error message
                }
            }
        }
    }, [dataDispatch, selectedEntitySubscription, uiDispatch, unsavedSubscriptionOptions]);



    return (
        <EntityDetailsControlBar
            className={className}
            projectId={projectId}
            timeSpanOptions={timeSpanOptions}
            enablePrevButton
            enableNextButton={canEnableNextButton}
            showSubscriptionSettings={showSubscriptionSettings}
            subscriptionSettings={controlBarSubscriptionSettings}
            onTimeSpanChanged={onTimeSpanChanged}
            onTimestampChanged={onTimestampChanged}
            onAddTimestamp={onAddTimestamp}
            onRemoveTimestamp={onRemoveTimestamp}
            onPrevButtonClicked={onPrevButtonClicked}
            onNextButtonClicked={onNextButtonClicked}
            onSubscriptionToggle={onSubscriptionToggle}
            onSubscriptionOptionToggle={onSubscriptionOptionToggle}
            onCancelSubscriptionChanges={onCancelSubscriptionChanges}
            onSaveSubscriptionChanges={onSaveSubscriptionChanges}

            // Pass all the controlBarUpdatableProps to the control bar
            timeSpan={timeSpan}
            timestamps={timestamps}
            maxTimestampInputs={settings.maxTimestampInputs}
            minTimestampInputs={settings.minTimestampInputs}
            optimizeDateSelector={settings.optimizeDateSelector}
            firstDayOfWeek={settings.firstDayOfWeek as DayOfWeek}
        />
    );
};
